import type Dexie from "dexie";

export interface OwnedDbConfig {
  /** localStorage key used to remember which user the DB belongs to. */
  ownerKey: string;
  /** Returns the currently active user id, or null if logged out. */
  getCurrentOwnerId: () => string | null;
  /**
   * What to do when `ensureOwnership()` runs with no active owner id.
   * - `true` (default): wipe the DB — privacy at rest, the original behavior.
   * - `false`: leave the DB intact (treat "no owner" as "can't verify yet").
   *   Use when cached data must survive logout AND the post-login window where
   *   the auth token lands (loggedIn=true) before the user id is loaded — a
   *   null-owner wipe in that window would destroy the data before the id
   *   arrives. Cross-user isolation is unaffected: a genuinely different owner
   *   is still wiped by the mismatch branch once its id is known.
   */
  clearWhenOwnerless?: boolean;
}

export type OwnedDbOperationResult<T> =
  | { status: "completed"; value: T }
  | { status: "owner-changed" };

export interface OwnedDbHelpers<TDb extends Dexie = Dexie> {
  /** Lazily verifies the DB owner matches the current user; clears DB on mismatch. */
  ensureOwnership: () => Promise<void>;
  /** Forces the next ensureOwnership() call to re-check (use after logout/login). */
  invalidateOwnership: () => void;
  /**
   * Clears tables in the Dexie instance. A full clear (no `excludeTables`)
   * also forgets the owner key; a partial clear keeps the excluded tables AND
   * the owner key, so retained rows survive a same-user re-login.
   */
  clearAll: (excludeTables?: string[]) => Promise<void>;
  /** Runs an operation only while the expected owner exclusively owns the DB. */
  runForOwner: <T>(
    expectedOwnerId: string | null,
    operation: (db: TDb) => Promise<T>
  ) => Promise<OwnedDbOperationResult<T>>;
}

/**
 * Wraps a Dexie instance with ownership-aware lifecycle helpers so the
 * cached data only ever belongs to a single logged-in user. Lifted from
 * user-app's original DbService so multiple apps can share the pattern.
 */
export function createOwnedDbHelpers<TDb extends Dexie>(
  db: TDb,
  config: OwnedDbConfig
): OwnedDbHelpers<TDb> {
  // Clears, ownership checks and owner-bound operations share one queue, so a
  // write started for a previous owner can never interleave with the next
  // owner's access.
  let queueTail: Promise<unknown> = Promise.resolve();
  let ownershipCheck: {
    ownerId: string | null;
    promise: Promise<void>;
  } | null = null;

  const enqueue = <T>(operation: () => Promise<T>): Promise<T> => {
    const result = queueTail.then(operation, operation);
    queueTail = result.catch(() => undefined);
    return result;
  };

  const clearTables = async (excludeTables: string[] = []): Promise<void> => {
    const tables = db.tables.filter((t) => !excludeTables.includes(t.name));
    if (tables.length > 0) {
      await db.transaction("rw", tables, async () => {
        await Promise.all(tables.map((table) => table.clear()));
      });
    }
    // A partial clear (caller deliberately kept some tables, e.g. logout that
    // preserves drafts/recents) must also preserve the owner key: the retained
    // rows stay attributed to that owner so a later same-user re-login is seen
    // as a match and ensureOwnership does NOT wipe them. Only a full clear
    // (no excludes) forgets the owner.
    if (excludeTables.length === 0) {
      localStorage.removeItem(config.ownerKey);
    }
  };

  /** Hands the DB to `ownerId`, wiping it first if anyone else owned it. */
  const claimFor = async (ownerId: string | null): Promise<void> => {
    if (!ownerId) {
      await clearTables();
      return;
    }
    if (localStorage.getItem(config.ownerKey) !== ownerId) {
      await clearTables();
      localStorage.setItem(config.ownerKey, ownerId);
    }
  };

  const ensureOwnership = (): Promise<void> => {
    const ownerId = config.getCurrentOwnerId();
    if (!ownerId && config.clearWhenOwnerless === false) {
      // Survive-logout mode: no active owner yet, so ownership can't be
      // verified — leave the DB intact instead of wiping. Don't memoize:
      // the owner id arrives on a later call and must still be checked
      // then, otherwise a different user could read the previous owner's
      // data.
      return Promise.resolve();
    }

    if (ownershipCheck?.ownerId === ownerId) {
      return ownershipCheck.promise;
    }

    const promise = enqueue(() => claimFor(ownerId));
    ownershipCheck = { ownerId, promise };
    promise.catch(() => {
      if (ownershipCheck?.promise === promise) {
        ownershipCheck = null;
      }
    });
    return promise;
  };

  const invalidateOwnership = (): void => {
    ownershipCheck = null;
  };

  const clearAll = (excludeTables: string[] = []): Promise<void> => {
    ownershipCheck = null;
    return enqueue(() => clearTables(excludeTables));
  };

  const runForOwner = <T>(
    expectedOwnerId: string | null,
    operation: (database: TDb) => Promise<T>
  ): Promise<OwnedDbOperationResult<T>> =>
    enqueue(async (): Promise<OwnedDbOperationResult<T>> => {
      const isExpectedOwner = () =>
        config.getCurrentOwnerId() === expectedOwnerId;
      if (!isExpectedOwner()) {
        return { status: "owner-changed" };
      }

      await claimFor(expectedOwnerId);
      let value: T;
      try {
        value = await operation(db);
      } finally {
        // The owner switched mid-operation: drop whatever it wrote.
        if (!isExpectedOwner()) {
          ownershipCheck = null;
          await clearTables();
        }
      }
      return isExpectedOwner()
        ? { status: "completed", value }
        : { status: "owner-changed" };
    });

  return {
    ensureOwnership,
    invalidateOwnership,
    clearAll,
    runForOwner
  };
}
